import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { AnalyticsHooks, DemoChip } from '@frontend/analytics-shared';
import { LocationChip } from '@frontend/analytics-shared';
import {
  BarChartData,
  Chart,
  CustomLegendsData,
  FormatValue,
  PieChartData,
  YAxisLabelValueTick,
} from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { trackingIds } from '../../tracking-ids';
import { CompareLocationsButton } from '../compare-locations-button';
import { useGetCancellationsMetric } from './hooks/use-get-cancellations-metric';
import { usePaNavigate } from './hooks/use-pa-navigate';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';
import { useMonthlyTrendChartModal } from './monthly-trend-chart-modal';
import { PracticeAnalyticsInfoTips } from './practice-analytics-info-tips';

type CancelledPatientsState = {
  cancelled: number;
  cancelledOpportunity: number;
  cancelledUnscheduled: number;
  cancelledUnscheduledOpportunity: number;
  rescheduled: number;
  rescheduledOpportunity: number;
  totalPatients: number;
};

type ChartData = {
  barChartData?: BarChartData;
  cancelledPatients: CancelledPatientsState;
  cancelledPatientsChart?: PieChartData;
  cancelledUnscheduledPatientsChart?: PieChartData;
  rescheduledPatientsChart?: PieChartData;
  smallModeChartData?: PieChartData;
};

type RenderChart = {
  commonTooltipLabel?: string;
  customLegends: CustomLegendsData;
  data?: PieChartData;
  formatter: FormatValue;
  hideTooltip?: boolean;
  tooltipTitle?: string;
};

const colors = {
  cancelled: theme.colors.warning50,
  cancelledUnscheduled: theme.colors.critical50,
  rescheduled: theme.colors.success20,
  skipTipRemaining: theme.colors.neutral20,
};

const customLegendsIds = Object.keys(colors);

const defaultChartData: PieChartData = {
  groups: [],
};

export const CancellationsMetric = ({
  clickNoop,
  isDrillDownPage,
  onFetchStateChange,
}: PracticeAnalyticsTypes.MetricProps) => {
  const { t } = useTranslation('analytics');
  const navigate = usePaNavigate();
  const { isSmallMode } = useIsSmallMode();
  const { filters, isDemoAccount, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore(
    'filters',
    'isDemoAccount',
    'showDemoChipAndBanner'
  );
  const { locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });
  const [isLargeChart, setIsLargeChart] = useState<boolean>(false);
  const { aggregatedCancellationsData, isLoading, rawCancellationsData } = useGetCancellationsMetric();

  const monthlyTrendModal = useMonthlyTrendChartModal({
    defaultKey: 'percentage',
    metric: 'cancellations',
    title: t('Trend: Cancellations'),
    trackingId: trackingIds.practiceAnalytics.cancellationsTrend,
  });

  const multipleLocationsSelected = (filters.locations?.length || 0) > 1;
  const showLocationComparison = isDrillDownPage && multipleLocationsSelected;

  const labels = {
    cancelled: t('Cancelled Patients'),
    cancelledUnscheduled: t('Remaining Unscheduled'),
    rescheduled: t('Rescheduled'),
    totalPatients: t('Total Patients'),
    totalProduction: t('Total Production'),
  };

  const chartData: ChartData | undefined = useMemo(() => {
    if (!aggregatedCancellationsData || !rawCancellationsData) {
      return;
    }

    const { missedAppointments } = aggregatedCancellationsData;
    let barChartData: BarChartData = {
      groups: [],
    };

    const cancelledPatients = {
      cancelled: missedAppointments?.totals?.cancelled || 0,
      cancelledOpportunity: missedAppointments?.totals?.totalProductionRisk || 0,
      cancelledUnscheduled: missedAppointments?.totals?.cancelledUnscheduled || 0,
      cancelledUnscheduledOpportunity: missedAppointments?.totals?.unscheduledOpportunity || 0,
      rescheduled: missedAppointments?.totals?.rescheduled || 0,
      rescheduledOpportunity: missedAppointments?.totals?.recapturedProduction || 0,
      totalPatients: missedAppointments?.totals?.totalPersonsCount || 0,
    };

    let cancelledPatientsChart: PieChartData | undefined;
    let rescheduledPatientsChart: PieChartData | undefined;
    let cancelledUnscheduledPatientsChart: PieChartData | undefined;
    let smallModeChartData: PieChartData | undefined;

    if (isSmallMode) {
      smallModeChartData = {
        centerMetric: formatters.currency(
          cancelledPatients.rescheduledOpportunity + cancelledPatients.cancelledUnscheduledOpportunity
        ),
        groups: [
          {
            name: 'cancelledUnscheduled',
            value: cancelledPatients.cancelledUnscheduledOpportunity,
          },
          {
            name: 'rescheduled',
            value: cancelledPatients.rescheduledOpportunity,
          },
        ],
      };
    } else {
      cancelledPatientsChart = {
        centerMetric: formatters.currency(missedAppointments?.totals?.totalProductionRisk),
        groups: [
          {
            name: 'cancelled',
            value: cancelledPatients.cancelled,
          },
          {
            name: 'skipTipRemaining',
            value: cancelledPatients.totalPatients - cancelledPatients.cancelled,
          },
        ],
      };

      rescheduledPatientsChart = {
        centerMetric: formatters.currency(missedAppointments?.totals?.recapturedProduction),
        groups: [
          {
            name: 'rescheduled',
            value: cancelledPatients.rescheduled,
          },
          {
            name: 'skipTipRemaining',
            value: cancelledPatients.cancelled - cancelledPatients.rescheduled,
          },
        ],
      };

      cancelledUnscheduledPatientsChart = {
        centerMetric: formatters.currency(missedAppointments?.totals?.unscheduledOpportunity),
        groups: [
          {
            name: 'cancelledUnscheduled',
            value: cancelledPatients.cancelledUnscheduled,
          },
          {
            name: 'skipTipRemaining',
            value: cancelledPatients.cancelled - cancelledPatients.cancelledUnscheduled,
          },
        ],
      };
    }

    if (showLocationComparison) {
      barChartData = Object.entries(rawCancellationsData).reduce(
        (acc, [locationId, locationDetails]) => {
          const { totals = {} } = locationDetails?.location.missedAppointments || {};

          acc.groups.push({
            name: locationNames[locationId] || locationId,
            values: (
              ['cancelled', 'rescheduled', 'cancelledUnscheduled'] as Array<
                keyof PracticeAnalyticsTypes.MissedAppointmentsTotals
              >
            ).reduce((acc, key) => ({ ...acc, [key]: totals[key] || 0 }), {}),
          });
          return acc;
        },
        { groups: [] } as BarChartData
      );
    }

    return {
      barChartData,
      cancelledPatients,
      cancelledPatientsChart,
      cancelledUnscheduledPatientsChart,
      rescheduledPatientsChart,
      smallModeChartData,
    };
  }, [aggregatedCancellationsData, isSmallMode, locationNames, rawCancellationsData, showLocationComparison]);

  const barChartTotals = useMemo(() => {
    return chartData?.barChartData?.groups?.reduce<Record<string, number>>((acc, { values }) => {
      Object.entries(values).forEach(([key, value]) => {
        if (!acc[key]) {
          acc[key] = 0;
        }
        acc[key] += value;
      });
      return acc;
    }, {});
  }, [chartData?.barChartData]);

  const renderPieChart = useCallback(
    ({ commonTooltipLabel, customLegends, data, formatter, hideTooltip, tooltipTitle }: RenderChart) => {
      return (
        <div css={styles.pieChartWrapper}>
          <Chart.PieChart
            appearance={{
              collectiveTooltip: true,
              customTooltipTitle: () => tooltipTitle,
              hideTooltip,
              minWidth: 240,
            }}
            commonTooltipLabel={commonTooltipLabel}
            customLegendsIds={customLegendsIds}
            data={data ?? defaultChartData}
            formatValue={formatter}
          />
          <Chart.Legends customData={customLegends} size='medium' style={{ marginTop: theme.spacing(2) }} />
        </div>
      );
    },
    []
  );

  useEffect(() => {
    onFetchStateChange?.(isLoading);
  }, [isLoading, onFetchStateChange]);

  return (
    <>
      <Chart
        colors={colors}
        emptyStateConfig={{
          hideEmptyState: !!chartData?.cancelledPatients?.totalPatients,
        }}
        isLoading={isLoading}
        labels={labels}
        onClick={
          clickNoop
            ? undefined
            : () => {
                navigate({
                  to: `/analytics/practice/cancelled-appointments`,
                });
              }
        }
        trackingId={trackingIds.practiceAnalytics.cancellationsChart}
      >
        {isDrillDownPage ? (
          showDemoChipAndBanner && <DemoChip />
        ) : (
          <Chart.Header
            actions={[monthlyTrendModal.triggerProps]}
            bottomElement={multipleLocationsSelected ? <CompareLocationsButton /> : null}
            infoTip={<PracticeAnalyticsInfoTips tip='cancellations' />}
            leftElement={showDemoChipAndBanner ? <DemoChip /> : null}
            title={t('Cancellations')}
          />
        )}
        {showLocationComparison ? (
          <>
            <Chart.BarChart
              appearance={{
                showXAxis: true,
                customTooltipTitle: ({ groupName }) => <LocationChip locationName={groupName} noTruncating />,
                customYAxisTick: ({ labels, groupName, ...rest }) => {
                  return (
                    <YAxisLabelValueTick
                      {...rest}
                      label={labels?.[groupName] || groupName}
                      onChartSizeChange={(isLargeChart) => setIsLargeChart(isLargeChart)}
                      xTransitionLabel={-190}
                    />
                  );
                },
                groupsGap: 24,
                layout: 'vertical',
                margin: { right: 20, left: isLargeChart ? 140 : 40 },
                mode: 'stacked',
                showGridLines: true,
                showYAxis: true,
              }}
              data={chartData?.barChartData}
            />
            <Chart.Legends formatValue={formatters.value} values={barChartTotals} />
          </>
        ) : isSmallMode ? (
          <>
            {renderPieChart({
              customLegends: {
                cancelledUnscheduled: {
                  label: t('Unscheduled'),
                  value: formatters.currency(chartData?.cancelledPatients?.cancelledUnscheduledOpportunity),
                },
                rescheduled: {
                  label: t('Rescheduled'),
                  value: formatters.currency(chartData?.cancelledPatients?.rescheduled),
                },
                total: {
                  label: t('Total Cancelled Patients'),
                  value: formatters.currency(
                    (chartData?.cancelledPatients?.cancelledUnscheduledOpportunity || 0) +
                      (chartData?.cancelledPatients?.rescheduled || 0)
                  ),
                },
              },
              data: chartData?.smallModeChartData,
              formatter: formatters.value,
              hideTooltip: true,
              tooltipTitle: t('Cancelled'),
            })}
          </>
        ) : (
          <>
            <Chart.Legends
              customData={{
                totalPatients: {
                  value: formatters.value(chartData?.cancelledPatients?.totalPatients),
                },
              }}
              size='medium'
            />
            <Chart.HorizontalContainer grid={3} style={{ marginBottom: 0 }}>
              {renderPieChart({
                commonTooltipLabel: t('Patients'),
                customLegends: {
                  cancelled: {
                    value: formatters.value(chartData?.cancelledPatients?.cancelled),
                  },
                },
                data: chartData?.cancelledPatientsChart,
                formatter: formatters.value,
                tooltipTitle: t('Cancelled'),
              })}

              {renderPieChart({
                commonTooltipLabel: t('Patients'),
                customLegends: {
                  rescheduled: {
                    value: formatters.value(chartData?.cancelledPatients?.rescheduled),
                  },
                },
                data: chartData?.rescheduledPatientsChart,
                formatter: formatters.value,
                tooltipTitle: t('Rescheduled'),
              })}

              {renderPieChart({
                commonTooltipLabel: t('Patients'),
                customLegends: {
                  cancelledUnscheduled: {
                    value: formatters.value(chartData?.cancelledPatients?.cancelledUnscheduled),
                  },
                },
                data: chartData?.cancelledUnscheduledPatientsChart,
                formatter: formatters.value,
                tooltipTitle: t('Remaining Unscheduled'),
              })}
            </Chart.HorizontalContainer>
          </>
        )}
      </Chart>
      {monthlyTrendModal.modalRenderer()}
    </>
  );
};

const styles = {
  pieChartWrapper: css`
    align-items: center;
    display: flex;
    flex-direction: column;

    .sneak-peak-item.vertical {
      align-items: flex-start;
      gap: ${theme.spacing(1)};
    }
  `,
};
